import { Backdrop } from '@mui/material';
import UPButton from 'components/button/UPButton';
import { UPDropdown } from 'components/dropdown';
import UPInput from 'components/input';
import toNumber from 'lodash/toNumber';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import {
  geocodeCostCenterAddressAction,
  postCostCenterAction,
  putCostCenterAction,
} from 'store/actions/company-cost-center-actions';
import { getLocationsAction, getLocationsDropdownItemsAction } from 'store/actions/company-locations-actions';
import { locationsDropdownItemsSelector, locationsSelector } from 'store/selectors/company-locations-selectors';
import { payrollSelector } from 'store/selectors/payroll-selectors';
import theme from 'theme';
import IPayrollCostCenter from 'types/company/payroll/IPayrollCostCenter';
import { allowOnlyPositiveNumber, getInputErrorText } from 'utils/helpers';
import { ReactComponent as CloseIcon } from '../../../assets/icons/Close.svg';
import {
  COMPANY_INFORMATION__BUS_NUMBER,
  COMPANY_INFORMATION__CITY,
  COMPANY_INFORMATION__POSTAL_CODE,
  COMPANY_INFORMATION__STREET_NAME,
  COMPANY_INFORMATION__STREET_NUMBER,
  COMPANY_MANAGERS__LOCATIONS,
  COMPANY_PAYROLL__COST_CENTER_ADDRESS_REQUIRED_FIRST,
  COMPANY_PAYROLL__COST_CENTER_COORDINATES_NOT_RESOLVED,
  COMPANY_PAYROLL__COST_CENTER_DESCRIPTION,
  COMPANY_PAYROLL__COST_CENTER_GET_COORDINATES,
  COMPANY_PAYROLL__COST_CENTER_INVALID_COORDINATES,
  COMPANY_PAYROLL__COST_CENTER_LATITUDE,
  COMPANY_PAYROLL__COST_CENTER_LONGITUDE,
  GENERAL__CANCEL_BUTTON,
  GENERAL__SAVE_BUTTON,
  GENERAL__SELECT,
} from '../../../translations/constants';
import CostCenterMiniMap from './CostCenterMiniMap';
import {
  CoordinatesInputContainer,
  ErrorText,
  NumberAndPostalBox,
  PayrollEditFunctionModal,
  PayrollEditFunctionModalActionButton,
  PayrollEditFunctionModalActionContainer,
  PayrollEditFunctionModalCloseIcon,
  PayrollEditFunctionModalColumn,
  PayrollEditFunctionModalContainer,
  PayrollEditFunctionModalInputBox,
  PayrollEditFunctionModalInputContainer,
  PayrollEditFunctionModalTitle,
} from './styles';
import { EditPayrollCostCenterProps } from './types';

const CostCenterInitialState: IPayrollCostCenter = {
  id: 0,
  name: '',
  streetName: '',
  streetNumber: '',
  busNumber: '',
  city: '',
  postalCode: '',
  locationId: '',
  latitude: undefined,
  longitude: undefined,
};

interface CoordinateInputProps {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  label: string;
  placeholder: string;
  error: boolean;
  helperText: string;
}

// Holds the raw typed text so intermediate values like "45." or "45.0" survive.
// Binding the input directly to the numeric form value strips them, because
// Number("45.") === 45 re-renders the field as "45" and eats the dot.
const CoordinateInput = ({
  value,
  onChange,
  label,
  placeholder,
  error,
  helperText,
}: CoordinateInputProps): JSX.Element => {
  const [text, setText] = useState(value === undefined || value === null ? '' : String(value));

  useEffect(() => {
    // Re-sync only when the numeric value changes from the outside (geocode, edit load,
    // clear) — not while the user is mid-edit typing an equivalent number.
    const parsed = text.trim() === '' ? undefined : Number(text);
    const sameNumber =
      parsed === value ||
      (typeof parsed === 'number' && typeof value === 'number' && Number.isNaN(parsed) && Number.isNaN(value));
    if (!sameNumber) {
      setText(value === undefined || value === null ? '' : String(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <UPInput
      value={text}
      onChange={ev => {
        const raw = ev?.target?.value ?? '';
        setText(raw);
        onChange(raw.trim() === '' ? undefined : Number(raw));
      }}
      placeholder={placeholder}
      label={label}
      error={error}
      helperText={helperText}
    />
  );
};

const EditPayrollCostCenter = (props: EditPayrollCostCenterProps): JSX.Element => {
  const [t] = useTranslation();
  const dispatch = useAppDispatch();

  let { companyId } = useParams();

  const { open, onClose, costCenter, title, isEdit, paramCompanyId, customDescription, locationId, onSave } = props;

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<IPayrollCostCenter>({
    defaultValues: costCenter || CostCenterInitialState,
  });

  const [isGeocoding, setIsGeocoding] = useState<boolean>(false);
  const [geocodeWarning, setGeocodeWarning] = useState<string>('');

  const watchedLatitude = watch('latitude');
  const watchedLongitude = watch('longitude');

  const locationsDropdownItems = useAppSelector(locationsDropdownItemsSelector);
  const locationDetails = useAppSelector(locationsSelector);
  const payroll = useAppSelector(payrollSelector);

  const locations = useMemo(() => locationsDropdownItems, [locationsDropdownItems]);

  if (!companyId) {
    companyId = paramCompanyId;
  }

  const setLocationDetails = (value: number) => {
    const selectedLocation = locationDetails.find(i => {
      return i.id === value;
    });

    setValue('streetName', selectedLocation?.streetName);
    setValue('streetNumber', selectedLocation?.streetNumber);
    setValue('busNumber', selectedLocation?.busNumber);
    setValue('city', selectedLocation?.city);
    setValue('postalCode', selectedLocation?.postalCode);
  };

  if (locationId) {
    setValue('locationId', locationId);
    setLocationDetails(toNumber(locationId));
  }

  if (customDescription) {
    setValue('name', customDescription);
  }

  useEffect(() => {
    dispatch(getLocationsDropdownItemsAction({ companyId: toNumber(companyId) }));
  }, [companyId, dispatch]);

  useEffect(() => {
    dispatch(getLocationsAction(toNumber(companyId)));
  }, [companyId, dispatch]);

  const resetForm = () => {
    setValue('streetName', null);
    setValue('streetNumber', null);
    setValue('busNumber', null);
    setValue('city', null);
    setValue('postalCode', null);
    setValue('locationId', null);
    setValue('name', null);
    setValue('latitude', undefined);
    setValue('longitude', undefined);
    setGeocodeWarning('');
  };

  const resolveCoordinates = async (): Promise<boolean> => {
    if (isGeocoding) {
      return false; // guards against rapid double-click -> single resolution
    }
    const { streetName, streetNumber, busNumber, city, postalCode } = getValues();
    if (!streetName || !streetNumber || !city || !postalCode) {
      setGeocodeWarning(t(COMPANY_PAYROLL__COST_CENTER_ADDRESS_REQUIRED_FIRST));
      return false;
    }

    setIsGeocoding(true);
    setGeocodeWarning('');
    try {
      const result = await dispatch(
        geocodeCostCenterAddressAction({
          companyId,
          address: { streetName, streetNumber, busNumber, city, postalCode },
        }),
      ).unwrap();

      if (result.resolved && result.latitude !== null && result.longitude !== null) {
        // Idempotent: same address -> backend returns the same point; we simply
        // overwrite with the resolved value (no duplicate records created).
        setValue('latitude', result.latitude, { shouldValidate: true });
        setValue('longitude', result.longitude, { shouldValidate: true });
        return true;
      }

      // resolved:false (a 200) -> could not geocode: warn, leave existing coords
      // unchanged, allow manual entry. Prefer the backend message when present.
      setGeocodeWarning(result.message || t(COMPANY_PAYROLL__COST_CENTER_COORDINATES_NOT_RESOLVED));
      return false;
    } catch (error) {
      // Rare HTTP/network failure (e.g. 400 no-address): same UX as not-resolved.
      setGeocodeWarning(t(COMPANY_PAYROLL__COST_CENTER_COORDINATES_NOT_RESOLVED));
      return false;
    } finally {
      setIsGeocoding(false);
    }
  };

  const onSaveCostCenter = handleSubmit(async (data, event) => {
    if (!companyId) {
      companyId = paramCompanyId;
    }

    if (isEdit) {
      dispatch(putCostCenterAction({ companyId, costCenter: data }));
    } else {
      const result = await dispatch(postCostCenterAction({ companyId, costCenter: data })).unwrap();
      if (result && onSave) {
        onSave(result);
      }
    }

    resetForm();

    onClose(event);
  });

  const onLocationChange = (e, onChange) => {
    const {
      target: { value },
    } = e;

    onChange(value);

    setLocationDetails(toNumber(value));
  };

  return (
    <Backdrop open={open}>
      <PayrollEditFunctionModal open={open} onClose={onClose} aria-labelledby="modal-edit-payroll-function">
        <PayrollEditFunctionModalContainer>
          <PayrollEditFunctionModalTitle>{title}</PayrollEditFunctionModalTitle>
          <PayrollEditFunctionModalCloseIcon onClick={onClose}>
            <CloseIcon fill={theme.palette.secondary.contrastText} />
          </PayrollEditFunctionModalCloseIcon>
          <PayrollEditFunctionModalInputContainer>
            <PayrollEditFunctionModalColumn sx={{ marginRight: '16px' }}>
              <PayrollEditFunctionModalInputBox>
                <Controller
                  name="locationId"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <UPDropdown
                      items={locations}
                      value={value}
                      onChange={newValue => onLocationChange(newValue, onChange)}
                      placeholder={t(GENERAL__SELECT)}
                      label={t(COMPANY_MANAGERS__LOCATIONS)}
                      error={!!errors.locationId}
                      required
                      helperText={getInputErrorText(errors, 'locationId')}
                    />
                  )}
                />
              </PayrollEditFunctionModalInputBox>
              <PayrollEditFunctionModalInputBox>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: true, maxLength: 100 }}
                  render={({ field }) => (
                    <UPInput
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t(COMPANY_PAYROLL__COST_CENTER_DESCRIPTION)}
                      label={t(COMPANY_PAYROLL__COST_CENTER_DESCRIPTION)}
                      required
                      error={!!errors.name}
                      helperText={getInputErrorText(errors, 'name', undefined, 100)}
                    />
                  )}
                />
              </PayrollEditFunctionModalInputBox>
              <PayrollEditFunctionModalInputBox>
                <Controller
                  name="streetName"
                  control={control}
                  rules={{ required: true, maxLength: 100 }}
                  render={({ field: { value, onChange } }) => (
                    <UPInput
                      value={value}
                      onChange={onChange}
                      placeholder={t(COMPANY_INFORMATION__STREET_NAME)}
                      label={t(COMPANY_INFORMATION__STREET_NAME)}
                      required
                      error={Boolean(errors.streetName)}
                      helperText={getInputErrorText(errors, 'streetName', undefined, 100)}
                    />
                  )}
                />
              </PayrollEditFunctionModalInputBox>
              <CoordinatesInputContainer>
                <PayrollEditFunctionModalInputBox>
                  <Controller
                    name="latitude"
                    control={control}
                    rules={{
                      validate: value =>
                        value === undefined ||
                        value === null ||
                        (typeof value === 'number' && !Number.isNaN(value) && value >= -90 && value <= 90),
                    }}
                    render={({ field: { value, onChange } }) => (
                      <CoordinateInput
                        value={value}
                        onChange={onChange}
                        placeholder={t(COMPANY_PAYROLL__COST_CENTER_LATITUDE)}
                        label={t(COMPANY_PAYROLL__COST_CENTER_LATITUDE)}
                        error={!!errors.latitude}
                        helperText={errors.latitude ? t(COMPANY_PAYROLL__COST_CENTER_INVALID_COORDINATES) : ''}
                      />
                    )}
                  />
                </PayrollEditFunctionModalInputBox>
                <PayrollEditFunctionModalInputBox>
                  <Controller
                    name="longitude"
                    control={control}
                    rules={{
                      validate: value =>
                        value === undefined ||
                        value === null ||
                        (typeof value === 'number' && !Number.isNaN(value) && value >= -180 && value <= 180),
                    }}
                    render={({ field: { value, onChange } }) => (
                      <CoordinateInput
                        value={value}
                        onChange={onChange}
                        placeholder={t(COMPANY_PAYROLL__COST_CENTER_LONGITUDE)}
                        label={t(COMPANY_PAYROLL__COST_CENTER_LONGITUDE)}
                        error={!!errors.longitude}
                        helperText={errors.longitude ? t(COMPANY_PAYROLL__COST_CENTER_INVALID_COORDINATES) : ''}
                      />
                    )}
                  />
                </PayrollEditFunctionModalInputBox>
                <PayrollEditFunctionModalInputBox>
                  <UPButton
                    text={t(COMPANY_PAYROLL__COST_CENTER_GET_COORDINATES)}
                    outlined
                    disabled={isGeocoding}
                    onClick={resolveCoordinates}
                  />
                  {geocodeWarning && <ErrorText>{geocodeWarning}</ErrorText>}
                </PayrollEditFunctionModalInputBox>
              </CoordinatesInputContainer>
            </PayrollEditFunctionModalColumn>
            <PayrollEditFunctionModalColumn>
              <PayrollEditFunctionModalInputBox>
                <Controller
                  name="busNumber"
                  control={control}
                  render={({ field }) => (
                    <UPInput
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t(COMPANY_INFORMATION__BUS_NUMBER)}
                      label={t(COMPANY_INFORMATION__BUS_NUMBER)}
                    />
                  )}
                />
              </PayrollEditFunctionModalInputBox>
              <PayrollEditFunctionModalInputBox>
                <Controller
                  name="city"
                  control={control}
                  rules={{ required: true, maxLength: 100 }}
                  render={({ field }) => (
                    <UPInput
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t(COMPANY_INFORMATION__CITY)}
                      label={t(COMPANY_INFORMATION__CITY)}
                      required
                      error={!!errors.city}
                      helperText={getInputErrorText(errors, 'city', undefined, 100)}
                    />
                  )}
                />
              </PayrollEditFunctionModalInputBox>
              <NumberAndPostalBox>
                <Controller
                  name="streetNumber"
                  control={control}
                  rules={{ required: true, maxLength: 100 }}
                  render={({ field: { value, onChange } }) => (
                    <UPInput
                      value={value}
                      onChange={ev => onChange(allowOnlyPositiveNumber(ev.target.value))}
                      placeholder={t(COMPANY_INFORMATION__STREET_NUMBER)}
                      label={t(COMPANY_INFORMATION__STREET_NUMBER)}
                      required
                      error={!!errors.streetNumber}
                      helperText={getInputErrorText(errors, 'streetNumber', undefined, 100)}
                    />
                  )}
                />
                <Controller
                  name="postalCode"
                  control={control}
                  rules={{ required: true, maxLength: 100 }}
                  render={({ field }) => (
                    <UPInput
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t(COMPANY_INFORMATION__POSTAL_CODE)}
                      label={t(COMPANY_INFORMATION__POSTAL_CODE)}
                      required
                      error={!!errors.postalCode}
                      helperText={getInputErrorText(errors, 'postalCode', undefined, 100)}
                    />
                  )}
                />
              </NumberAndPostalBox>
              <CoordinatesInputContainer>
                {/* The company's effective on-site radius (US 7390) is a company-wide value carried
                    on the payroll-general config; the map renders the circle automatically once passed. */}
                <CostCenterMiniMap
                  latitude={watchedLatitude}
                  longitude={watchedLongitude}
                  radiusMeters={payroll?.onSiteRadiusMeters}
                />
              </CoordinatesInputContainer>
            </PayrollEditFunctionModalColumn>
          </PayrollEditFunctionModalInputContainer>
          <PayrollEditFunctionModalActionContainer>
            <PayrollEditFunctionModalActionButton text={t(GENERAL__SAVE_BUTTON)} onClick={onSaveCostCenter} />
            <PayrollEditFunctionModalActionButton text={t(GENERAL__CANCEL_BUTTON)} outlined onClick={onClose} />
          </PayrollEditFunctionModalActionContainer>
        </PayrollEditFunctionModalContainer>
      </PayrollEditFunctionModal>
    </Backdrop>
  );
};

EditPayrollCostCenter.defaultProps = {
  costCenter: {
    id: 0,
    name: '',
    streetName: '',
    streetNumber: '',
    busNumber: '',
    city: '',
    postalCode: '',
  },
  isEdit: true,
};
export default EditPayrollCostCenter;
